import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ReportItem } from '../../core/models/report.models';
import { AuthService } from '../../core/services/auth.service';
import { ReportsService } from '../../core/services/reports.service';

@Component({
  selector: 'app-reports-page',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './reports-page.component.html',
  styleUrl: './reports-page.component.scss',
})
export class ReportsPageComponent implements OnInit {
  private readonly reportsService = inject(ReportsService);
  private readonly auth = inject(AuthService);

  loading = true;
  accessDenied = false;
  reports: ReportItem[] = [];
  allReports: ReportItem[] = [];
  searchQuery = '';
  currentPage = 0;
  pageSize = 20;
  totalPages = 1;
  totalItems = 0;
  errorMessage = '';

  ngOnInit(): void {
    this.loading = true;
    this.errorMessage = '';

    this.auth.refreshProfile().subscribe({
      next: (profile) => {
        if (!this.auth.hasAdminAccess(profile)) {
          this.accessDenied = true;
          this.loading = false;
          return;
        }
        this.accessDenied = false;
        this.reload(0);
      },
      error: () => {
        this.errorMessage = 'Impossible de vérifier le profil.';
        this.loading = false;
      },
    });
  }

  reload(page = this.currentPage): void {
    if (this.accessDenied) {
      this.loading = false;
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.reportsService.getReports('OPEN', page, this.pageSize).subscribe({
      next: (response) => {
        this.allReports = response.items ?? [];
        this.currentPage = response.currentPage ?? page;
        this.totalPages = Math.max(1, response.totalPages ?? 1);
        this.totalItems = response.total ?? this.allReports.length;
        this.applySearch(this.searchQuery, false);
      },
      error: (error) => {
        this.errorMessage = error?.error?.message ?? 'Impossible de charger les signalements.';
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  applySearch(query: string, resetPage = true): void {
    this.searchQuery = query;
    const normalized = this.searchQuery.trim().toLowerCase();
    this.reports = this.allReports.filter((report) => {
      if (!normalized) {
        return true;
      }
      const haystack = `${report.targetType} ${report.targetId} ${report.reason} ${report.message ?? ''}`.toLowerCase();
      return haystack.includes(normalized);
    });

    if (resetPage) {
      this.currentPage = 0;
    }
  }

  previousPage(): void {
    if (this.currentPage <= 0) {
      return;
    }
    this.reload(this.currentPage - 1);
  }

  nextPage(): void {
    if (this.currentPage + 1 >= this.totalPages) {
      return;
    }
    this.reload(this.currentPage + 1);
  }

  decide(report: ReportItem, decision: 'IGNORE' | 'REMOVE_CONTENT' | 'SUSPEND_ACCOUNT' | 'WARN_USER'): void {
    this.reportsService.decide(report.id, { decision }).subscribe({
      next: () => this.reload(),
      error: (error) => {
        this.errorMessage = error?.error?.message ?? 'Décision impossible.';
      },
    });
  }
}
